import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { productionFetchClient as fetchClient } from "../generated/client";
import { LoadOrderWithItemsResponseDto } from "../generated/schema";

type UseSearchPublicOrderParams = {
  providerId: string;
  orderId?: string | null;
  orderName?: string | null;
  secondaryInput?: string | null;
};

// TODO: this should be a POST

export function useSearchPublicOrder() {
  const client = useQueryClient();

  const mutation = useMutation({
    onSuccess: (data) => {
      client.setQueryData(["order", data.orderId], data);
    },

    mutationFn: async ({
      providerId,
      ...params
    }: UseSearchPublicOrderParams) => {
      const _params = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => !!v),
      );

      const response = await fetchClient.GET(
        "/public/orders/load-with-items",
        {
          params: {
            query: { providerId, ..._params },
            header: { authorization: "" },
          },
        },
      );

      // TODO handle errors
      if (response.error || response.data.error) {
        throw (
          response.error ?? response.data.error ?? new Error("Unknown error")
        );
      }

      if (!response.data.data) throw new Error("No data returned");

      return response.data.data;
    },
  });

  return mutation;
}

export function usePublicOrderWithItems<T = LoadOrderWithItemsResponseDto>(
  id: string,
  {
    select,
  }: { select?: (data: LoadOrderWithItemsResponseDto["data"]) => T } = {},
) {
  return useSuspenseQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      const data = localStorage.getItem(`${CACHED_ORDER_PREFIX}-${id}`);
      if (!data) throw new Error("Should be cached (?!)");
      return JSON.parse(data);
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,
    select: select!,
  });
}

export const CACHED_ORDER_PREFIX = "cached-order";

export async function fetchOrder(params: UseSearchPublicOrderParams) {
  const { providerId, ..._params } = params;

  const filteredParams = Object.fromEntries(
    Object.entries(_params).filter(([_, v]) => !!v),
  );

  const response = await fetchClient.GET(
    "/public/orders/load-with-items",
    {
      params: {
        query: { providerId, ...filteredParams },
        header: { authorization: "" },
      },
    },
  );

  if (response.error || response.data.error) {
    throw response.error ?? response.data.error ?? new Error("Unknown error");
  }

  if (!response.data.data) throw new Error("No data returned");

  localStorage.setItem(
    `${CACHED_ORDER_PREFIX}-${response.data.data.orderId}`,
    JSON.stringify(response.data.data),
  );

  return response.data.data;
}
